// The one exception to "one HTTP client" (proposal 6.6, lead's decision of 2026-10-14): the file
// itself goes from the browser straight to the bucket, under the policy the server signed at
// prepare. This module posts only the url and fields of that answer, with no header of ours; to
// the GCS host it is cross-origin without credentials, so nothing of the session reaches the
// storage host (audit frontend B.7). XMLHttpRequest, not fetch, because it reports upload
// progress; the lint allows it only here.
import { TransportError } from "./errors";
import type { UploadTicket } from "./generated/model";

// Where a signed upload may go: Google Cloud Storage. Outside a production build also this
// origin, where the fake API's bucket lives (`just web-dev`, the smoke's `--mode e2e` build); a
// same-origin post would carry the session cookie, so a production build never allows it.
const BUCKET_ORIGIN = "https://storage.googleapis.com";

export function assertBucketUrl(url: string): URL {
  const parsed = new URL(url, window.location.origin);
  const sameOrigin =
    import.meta.env.MODE !== "production" && parsed.origin === window.location.origin;
  if (parsed.origin !== BUCKET_ORIGIN && !sameOrigin) {
    throw new Error(`An upload goes only to the bucket, not to ${parsed.origin}`);
  }
  return parsed;
}

export interface SendOptions {
  /** 0 to 1, as the browser reports the bytes sent. */
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
}

/**
 * Posts `file` to the bucket with the ticket's form: every field in order, then the file as the
 * last field `file` (S1's `UploadForm`). A ticket without a form means the bucket already has
 * the file, so nothing is sent. Rejects with TransportError (`network`, or `http` with the
 * bucket's status) or the AbortError of `signal`.
 */
export async function sendToBucket(
  // Any ticket with S1's form: the library's, or a voice's (G7-team-voices).
  ticket: Pick<UploadTicket, "form">,
  file: Blob,
  options: SendOptions = {},
): Promise<void> {
  const form = ticket.form;
  if (form === null || form === undefined) return;
  const url = assertBucketUrl(form.url);
  const body = new FormData();
  for (const [name, value] of Object.entries(form.fields)) body.append(name, value);
  body.append("file", file);

  // A listener on `xhr.upload` makes the browser send a CORS preflight first; the bucket's CORS
  // rule for the app origin answers it (F1b).
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const abort = () => xhr.abort();
    const done = () => options.signal?.removeEventListener("abort", abort);
    xhr.open("POST", url.href);
    xhr.withCredentials = false;
    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) options.onProgress?.(event.loaded / event.total);
    });
    xhr.addEventListener("load", () => {
      done();
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new TransportError("http", xhr.status));
    });
    xhr.addEventListener("error", () => {
      done();
      reject(new TransportError("network"));
    });
    xhr.addEventListener("abort", () => {
      done();
      reject(new DOMException("The upload was canceled.", "AbortError"));
    });
    if (options.signal?.aborted === true) {
      reject(new DOMException("The upload was canceled.", "AbortError"));
      return;
    }
    options.signal?.addEventListener("abort", abort);
    xhr.send(body);
  });
}
